import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { IEmployee } from '@/types/employer';
import { Search } from '@mui/icons-material';
import { TextField } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';
import debounce from 'lodash.debounce';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useGetEmployees } from '../../hooks/useEmployees';
import { useLearningBudgetManagement } from '../../hooks/useLearningBudgetManagement';
import { EmployeesSearchParams } from '../../types';
import { ConfirmDeleteMemberModal } from '../ConfirmDeleteMemberModal/ConfirmDeleteMemberModal';
import { ListEmployeesTable } from '../ListEmployeesTable';

export function ListEmployeesTab() {
  const userContext = useCurrentUserContext();
  const navigate = useNavigate();
  const search = useSearch({
    strict: false,
  }) as EmployeesSearchParams;
  const [memberToDeleteId, setMemberToDeleteId] = useState<string | null>(null);

  const {
    isLoading,
    isRefetching,
    refetch: refetchEmployees,
    data: { data: employees = [], metadata: { total = 0, page = 1 } = {} } = {},
  } = useGetEmployees(
    userContext?.employer?._id as string,
    { email: search.email },
    {
      page: search.page || 1,
      limit: search.limit || 10,
    },
    {
      placeholderData: keepPreviousData,
      enabled:
        !!userContext?.employer?._id &&
        (!search.email || search.email.length > 3),
    },
  );

  const { Modal: LearningBudgetAllocationModal, showModal } =
    useLearningBudgetManagement({
      afterUpdate: refetchEmployees,
    });

  const paginationState = {
    pageIndex: (page ?? 1) - 1 || 0,
    pageSize: search.limit || 10,
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    debouncedSearchTerm(value);
  };

  const onPaginationChange = ({ pageIndex, pageSize }: PaginationState) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: pageIndex + 1,
        limit: pageSize,
      }),
    });
  };

  const debouncedSearchTerm = useMemo(() => {
    return debounce(term => {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          email: term,
        }),
      });
    }, 300);
  }, [navigate]);

  const handleEmployeeLearningBudgetChange = (employee: IEmployee) => {
    showModal({
      variant: 'user',
      user: employee,
    });
  };

  const showMemberDeletionModal = (employee: IEmployee) => {
    setMemberToDeleteId(employee._id);
  };

  const handleMemberDeletion = async (result?: { confirm: boolean }) => {
    try {
      if (!result?.confirm || !memberToDeleteId) {
        return;
      }

      throw new Error('Not implemented');
    } catch {
      enqueueSnackbar('Failed to delete member. Please try again.', {
        variant: 'error',
      });
    } finally {
      setMemberToDeleteId(null);
    }
  };

  useEffect(() => {
    return () => {
      debouncedSearchTerm.cancel();
    };
  }, [debouncedSearchTerm]);

  return (
    <>
      <div className="flex gap-12 py-3 px-8">
        <TextField
          id="employeeName"
          size="small"
          slotProps={{
            input: {
              endAdornment: <Search className="ml-3" />,
              className: 'focus:outline-none',
              autoFocus: !!search.email,
            },
          }}
          className="ml-auto"
          placeholder="Search for employees"
          onChange={onInputChange}
        />
      </div>
      <div className="flex-1 px-8 flex flex-col">
        <ListEmployeesTable
          tableOptions={{
            onPaginationChange: updater => {
              return typeof updater === 'function'
                ? onPaginationChange(updater(paginationState))
                : onPaginationChange(updater);
            },
            data: employees,
            state: {
              isLoading: isLoading || isRefetching,
              pagination: paginationState,
            },
            rowCount: total,
          }}
          onManageLearningBudgetFunds={handleEmployeeLearningBudgetChange}
          onDeleteEmployee={showMemberDeletionModal}
        />
      </div>
      <LearningBudgetAllocationModal />
      <ConfirmDeleteMemberModal
        open={!!memberToDeleteId}
        onClose={handleMemberDeletion}
      />
    </>
  );
}
